"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Countdown from "./Countdown";
import EditableElement from "@/components/customize/EditableElement";

export default function Events({
  heading,
  subtitle,
  events = [],
  countdownDate,
  dresscode,
  templateMeta,
  __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __onFileSelected,
  __focusFieldName,
}) {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  const sectionTitleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";
  const eventTextClass = __isMobile ? "text-xs" : "text-xs md:text-sm";

  const formatDateDisplay = (dateString, formatType) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    const dayNameEn = d.toLocaleDateString("en-US", { weekday: "long" });
    const monthLongEn = d.toLocaleDateString("en-US", { month: "long" });
    const monthShortEn = d.toLocaleDateString("en-US", { month: "short" });

    const dayNameId = d.toLocaleDateString("id-ID", { weekday: "long" });
    const monthLongId = d.toLocaleDateString("id-ID", { month: "long" });
    const monthShortId = d.toLocaleDateString("id-ID", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();

    const getSuffix = (n) => {
      if (n >= 11 && n <= 13) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    switch (formatType) {
      case "short":
        return `${monthShortEn} ${day}, ${year}`;
      case "numeric":
        return `${String(day).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${year}`;
      case "full":
        return `${dayNameEn}, ${monthLongEn} ${day}${getSuffix(day)}, ${year}`;
      case "id-long":
        return `${dayNameId}, ${day} ${monthLongId} ${year}`;
      case "id-short":
        return `${day} ${monthShortId} ${year}`;
      case "long":
      default:
        return `${dayNameEn}, ${day} ${monthLongEn} ${year}`;
    }
  };

  const getEventDate = (event, idx) => {
    if (idx === 0 && templateMeta?.marriageContractDate) return templateMeta.marriageContractDate;
    if (idx === 1 && templateMeta?.receptionDate) return templateMeta.receptionDate;
    return event.date;
  };

  const getEventTimeStart = (event, idx) => {
    if (idx === 0 && templateMeta?.marriageContractDate) {
      const d = new Date(templateMeta.marriageContractDate);
      if (!isNaN(d.getTime())) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    if (idx === 1 && templateMeta?.receptionDate) {
      const d = new Date(templateMeta.receptionDate);
      if (!isNaN(d.getTime())) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    return event.timeStart;
  };

  const getEventTimeEnd = (event, idx) => {
    if (idx === 0 && templateMeta?.marriageContractTimeEnd) {
      return templateMeta.marriageContractTimeEnd;
    }
    if (idx === 1 && templateMeta?.receptionTimeEnd) {
      return templateMeta.receptionTimeEnd;
    }
    return event.timeEnd;
  };

  const activeCountdownDate = templateMeta?.countdownSource === "reception"
    ? templateMeta?.receptionDate
    : templateMeta?.marriageContractDate;
  const finalCountdownDate = activeCountdownDate || countdownDate;

  return (
    <div className="text-black bg-white px-6 py-12 max-w-6xl mx-auto space-y-16">
      {/* Section Heading */}
      {(heading || subtitle) && (
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          className="text-center"
        >
          {heading && (
            <EditableElement
              type="text"
              value={heading}
              onChange={(val) => __onFieldChange?.(__sectionId, "heading", val)}
              isEditable={__isEditable}
              fieldName="heading"
              onEditStart={() => __onFieldEdit?.(__sectionId, "heading")}
              isActive={__focusFieldName === "heading"}
              isMobile={__isMobile}
            >
              <p className={`font-cormorant font-bold ${sectionTitleClass}`}>{heading}</p>
            </EditableElement>
          )}
          {subtitle && (
            <EditableElement
              type="text"
              value={subtitle}
              onChange={(val) => __onFieldChange?.(__sectionId, "subtitle", val)}
              isEditable={__isEditable}
              fieldName="subtitle"
              onEditStart={() => __onFieldEdit?.(__sectionId, "subtitle")}
              isActive={__focusFieldName === "subtitle"}
              isMobile={__isMobile}
            >
              <p className="text-xs font-sans text-black">{subtitle}</p>
            </EditableElement>
          )}
        </motion.div>
      )}

      {/* Event Cards */}
      <div className="grid grid-cols-1 gap-10">
        {events.map((event, idx) => (
          <motion.div
            key={idx}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInUp}
            className="relative overflow-hidden text-white"
          >
            <div className="absolute inset-0 z-0">
              <EditableElement
                type="image"
                value={event.image}
                onChange={(val) => __onFieldChange?.(__sectionId, `events[${idx}].image`, val)}
                isEditable={__isEditable}
                fieldName={`events[${idx}].image`}
                onFileSelected={(file, url) => __onFileSelected?.(`defaultSections.${__sectionId}.props.events[${idx}].image`, file, url)}
                className="absolute inset-0 w-full h-full"
              >
                {typeof event.image === "string" && event.image.trim() !== "" && (
                  <Image
                    src={event.image}
                    alt={event.title || "Event"}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
                  />
                )}
              </EditableElement>
              <div className="absolute inset-0 bg-linear-to-r from-black/60 to-transparent bg-black/60" />
            </div>
            <div className="relative z-10 px-8 py-8">
              <EditableElement
                type="text"
                value={event.title}
                onChange={(val) => __onFieldChange?.(__sectionId, `events[${idx}].title`, val)}
                isEditable={__isEditable}
                fieldName={`events[${idx}].title`}
                onEditStart={() => __onFieldEdit?.(__sectionId, `events[${idx}].title`)}
                isActive={__focusFieldName === `events[${idx}].title`}
                isMobile={__isMobile}
                className="block mb-1"
              >
                <h3 className="font-caudex mb-3 font-bold text-xl">{event.title}</h3>
              </EditableElement>
              {(getEventTimeStart(event, idx) || getEventTimeEnd(event, idx)) && (
                <p className={`${eventTextClass} block mb-1`}>
                  {getEventTimeStart(event, idx) || ""} {getEventTimeStart(event, idx) && getEventTimeEnd(event, idx) ? "-" : ""} {getEventTimeEnd(event, idx) || ""}
                </p>
              )}
              {getEventDate(event, idx) && (
                <EditableElement
                  type="text"
                  value={formatDateDisplay(getEventDate(event, idx), event.dateFormat || "long")}
                  isEditable={__isEditable}
                  fieldName={`events[${idx}].dateDisplay`}
                  onEditStart={() => {
                    if (idx === 0) __onFieldEdit?.("general", "marriageContractDate");
                    else if (idx === 1) __onFieldEdit?.("general", "receptionDate");
                  }}
                  useContentEditable={false}
                  className="block mb-1"
                >
                  <p className={`${eventTextClass} font-light`}>{formatDateDisplay(getEventDate(event, idx), event.dateFormat || "long")}</p>
                </EditableElement>
              )}
              {event.venue && (
                <EditableElement
                  type="text"
                  value={event.venue}
                  onChange={(val) => __onFieldChange?.(__sectionId, `events[${idx}].venue`, val)}
                  isEditable={__isEditable}
                  fieldName={`events[${idx}].venue`}
                  onEditStart={() => __onFieldEdit?.(__sectionId, `events[${idx}].venue`)}
                  isActive={__focusFieldName === `events[${idx}].venue`}
                  isMobile={__isMobile}
                  className="block mb-1"
                >
                  <p className={eventTextClass}>{event.venue}</p>
                </EditableElement>
              )}
              {event.address && (
                <EditableElement
                  type="text"
                  value={event.address}
                  onChange={(val) => __onFieldChange?.(__sectionId, `events[${idx}].address`, val)}
                  isEditable={__isEditable}
                  fieldName={`events[${idx}].address`}
                  onEditStart={() => __onFieldEdit?.(__sectionId, `events[${idx}].address`)}
                  isActive={__focusFieldName === `events[${idx}].address`}
                  isMobile={__isMobile}
                  className="block mt-1"
                >
                  <p className={`flex items-start space-x-2 ${eventTextClass}`}>
                    <span>{event.address}</span>
                  </p>
                </EditableElement>
              )}
              <div className="flex space-x-2 mt-3">
                {event.ctas?.map((cta, ctaIdx) => (
                  <a
                    key={ctaIdx}
                    href={cta.href || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-black rounded-full py-1.5 px-4 text-sm"
                  >
                    {cta.label}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        ))}

        {/* Countdown */}
        {finalCountdownDate && <Countdown targetDate={finalCountdownDate} __isMobile={__isMobile} />}
      </div>

      {/* Dresscode */}
      {dresscode && (
        <motion.div
          className="text-center"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
        >
          <EditableElement
            type="text"
            value={dresscode.heading}
            onChange={(val) => __onFieldChange?.(__sectionId, "dresscode.heading", val)}
            isEditable={__isEditable}
            fieldName="dresscode.heading"
            onEditStart={() => __onFieldEdit?.(__sectionId, "dresscode.heading")}
            isActive={__focusFieldName === "dresscode.heading"}
            isMobile={__isMobile}
          >
            <p className={`font-semibold font-cormorant ${sectionTitleClass}`}>
              {dresscode.heading}
            </p>
          </EditableElement>
          <EditableElement
            type="text"
            value={dresscode.subtitle}
            onChange={(val) => __onFieldChange?.(__sectionId, "dresscode.subtitle", val)}
            isEditable={__isEditable}
            fieldName="dresscode.subtitle"
            onEditStart={() => __onFieldEdit?.(__sectionId, "dresscode.subtitle")}
            isActive={__focusFieldName === "dresscode.subtitle"}
            isMobile={__isMobile}
          >
            <p className="text-sm font-caudex text-black mb-6">{dresscode.subtitle}</p>
          </EditableElement>
          <div className="w-full mx-auto">
            <div className="relative w-full max-h-100 overflow-hidden ">
              <EditableElement
                type="image"
                value={dresscode.image}
                onChange={(val) => __onFieldChange?.(__sectionId, "dresscode.image", val)}
                isEditable={__isEditable}
                fieldName="dresscode.image"
                onFileSelected={(file, url) => __onFileSelected?.(`defaultSections.${__sectionId}.props.dresscode.image`, file, url)}
                className="w-full h-full"
              >
                {typeof dresscode.image === "string" && dresscode.image.trim() !== "" && (
                  <Image
                    src={dresscode.image}
                    alt="dresscode"
                    width={1050}
                    height={700}
                    className="w-full h-full object-cover object-center"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1050px"
                  />
                )}
              </EditableElement>
              {dresscode.colors?.length > 0 && (
                <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 flex gap-3 z-10">
                  {dresscode.colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="w-10 h-10 border-2 border-white shadow-md"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
